import React, { useContext, useEffect, useState } from "react";
import { useAuth } from "./auth";
import { SESSION_STORE, useIndexedDb } from "./indexedDb";

//context que gerencia temas do usuário
const ThemeContext = React.createContext({
	themeName: "black",
	changeTheme: () => { },
});

const themes = {
	global: {
		primaryColor: "#135bec",
		secondaryColor: "#4de058",
	},
	classic: {
		menuBackgroundColor: "#ffffff",
		menuLinkColor: "var(--textColor)",
		menuHoverColor: "#6abdfb6b",
		menuSelectedColor: "#0088c2",
		tableRowBorderColor: "#efefef",
		listItemSelectedColor: "#DDDDDD",
		buttonBackgroundColor: "var(--primaryColor)",
		buttonBorderColor: "#1b5593e0",
		buttonTextColor: "white",
		buttonWhiteBackgroundColor: "transparent",
		buttonWhiteTextColor: "var(--primaryColor)",
		buttonWhiteBorderColor: "var(--primaryColor)",
		condominiumsSelectBgColor: "#5085ad",
		condominiumsSelectTextColor: "white",
		condominiumsSelectBorderColor: "#5085ad",
		containerBackgroundColor: "white",
		bodyBackgroundColor: "#f6f6f8",
		listHeaderBackgroundColor: "var(--color-gray-50)",
		textColor: "#3D4D59",
		textColorDisabled: "#b8c4cd",
		textColorSecundary: "gray",
		borderColor: "#e5e7eb",
		internalBorderColor: "#f3f3f3",
		// inputBackgroundColor: "#FFFFFF",
		inputBackgroundColor: "var(--containerBackgroundColor)",
		inputBoxShadow: "none",
		tabSelectionLine: "black",
		receptionIconsColor: "#181818",
		inputBackgroundColorDisabled: "#f1efef",
		// "box-shadow": "0px 4px 8px 1px rgba(0, 0, 0, 0.07)",
		"box-shadow": "0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -2px rgba(0,0,0,0.08)",
		"box-shadow-md": "0 10px 15px -3px var(--tw-shadow-color,#0000001a), 0 4px 6px -4px var(--tw-shadow-color,#0000001a)",
		"box-shadow-lg": "0 10px 15px -3px var(--tw-shadow-color,#0000001a), 0 4px 6px -4px var(--tw-shadow-color,#0000001a)",
		"entryBorderColor": "rgba(29, 78, 216, 0.4)",
		"entryBackgroundColor": "rgba(29, 78, 216, 0.12)",
		"entryColor": "#1D4ED8",
		"exitBorderColor": "rgba(21, 128, 61, 0.4)",
		"exitBackgroundColor": "rgba(21, 128, 61, 0.13)",
		"exitColor": "#15803D",
		"onlineColor": "#65f165",
		"offlineColor": "#eb7163",
		"activeLicensesColor": "#05df72",
		"pendingLicensesColor": "#f7ba31",
		"expiredLicensesColor": "#ff6169",
		"upcomingDueLicensesColor": "#f7ba31",
		"blockedLicensesColor": "#c17cfa",
		"noDeviceLicensesColor": "#98a1ae",
		"eventDetailsLeftContainerColor": "#fcfbfb",
	},
	black: {
		menuBackgroundColor: "#182131",
		menuLinkColor: "white",
		menuHoverColor: "#8080804f",
		menuSelectedColor: "#0088c2",
		tableRowBorderColor: "#272727ff",
		listItemSelectedColor: "#182131",
		buttonBackgroundColor: "var(--primaryColor)",
		buttonBorderColor: "",
		buttonTextColor: "white",
		buttonWhiteBackgroundColor: "transparent",
		buttonWhiteBorderColor: "var(--primaryColor)",
		buttonWhiteTextColor: "var(--primaryColor)",
		condominiumsSelectBgColor: "#0B1416",
		condominiumsSelectTextColor: "white",
		condominiumsSelectBorderColor: "#555555",
		containerBackgroundColor: "#141d2c",
		bodyBackgroundColor: "#101622",
		listHeaderBackgroundColor: "#1e2936",
		textColor: "white",
		textColorDisabled: "#b8c4cd",
		textColorSecundary: "#b8c4cd",
		borderColor: "#374151",
		internalBorderColor: "#262626",
		// inputBackgroundColor: "#141c28",
		inputBackgroundColor: "var(--containerBackgroundColor)",
		inputBoxShadow: "none",
		tabSelectionLine: "white",
		receptionIconsColor: "white",
		inputBackgroundColorDisabled: "#1A282D",
		"box-shadow": "rgba(0, 0, 0, 0.3) 0px 2px 5px, rgba(0, 0, 0, 0.14) 0px 1px 4px",
		"box-shadow-md": "0 4px 8px 1px rgba(0, 0, 0, 0.3)",
		"box-shadow-lg": "0 4px 10px 3px rgba(0, 0, 0, 0.65)",
		"entryBorderColor": "rgba(59, 130, 246, 0.45)",
		"entryBackgroundColor": "rgba(59, 130, 246, 0.1)",
		"entryColor": "#3B82F6",
		"exitBorderColor": "rgba(34, 197, 94, 0.45)",
		"exitBackgroundColor": "rgba(34, 197, 94, 0.1)",
		"exitColor": "#22C55E",
		"onlineColor": "#0bf50b",
		"offlineColor": "#ad3224",
		"activeLicensesColor": "#05df72",
		"pendingLicensesColor": "#f7ba31",
		"expiredLicensesColor": "#ff6169",
		"upcomingDueLicensesColor": "#f7ba31",
		"blockedLicensesColor": "#c17cfa",
		"noDeviceLicensesColor": "#98a1ae",
		"eventDetailsLeftContainerColor": "#121a27",
	},
};

export const ThemeProvider = ({ children }) => {
	const { user } = useAuth();
	const [themeName, setThemeName] = useState({ name: 'black' });
	const [theme, setTheme] = useState(themes[themeName]);
	const { lastSessionData, save } = useIndexedDb();

	useEffect(() => {
		if (!user) {
			changeTheme('classic');
		}
	}, [])

	useEffect(() => {
		if (lastSessionData?.theme) {
			const themeName = parseThemeName(lastSessionData?.theme);
			setTheme(themes[themeName]);
			setThemeName({ name: themeName });
		}
	}, [lastSessionData]);

	useEffect(() => {
		if (user?.userData) {
			changeTheme(user?.userData?.theme ?? user?.userData?.selectedOrganization?.defaultTheme ?? 'black')
		}
	}, [user]);

	useEffect(() => {
		setCSSGlobalVariables(theme);
	}, [themeName]);

	function parseThemeName(themeName) {
		return (themeName === 'light' ? 'classic' : (themeName === 'dark' ? 'black' : themeName))
	}

	const setCSSGlobalVariables = (theme) => {
		document.body.setAttribute('data-theme', themeName.name);
		const isSelectedUnitSubOrganization = user?.userData?.selectedUnit?.isSubOrganization ?? false;
		const userPrimaryColor = isSelectedUnitSubOrganization ? user?.userData?.selectedUnit?.primaryColor : (user?.userData?.selectedSubOrganization?.primaryColor ?? user?.userData?.selectedOrganization?.primaryColor);
		const userSecondaryColor = isSelectedUnitSubOrganization ? user?.userData?.selectedUnit?.secondaryColor : (user?.userData?.selectedSubOrganization?.secondaryColor ?? user?.userData?.selectedOrganization?.secondaryColor);
		const primaries = ['primaryColor', 'menuSelectedColor', 'buttonBackgroundColor', 'buttonBorderColor', 'buttonWhiteTextColor', 'buttonWhiteBorderColor', 'menuHoverColor'];
		const secondaries = ['secondaryColor'];
		for (const [field, value] of Object.entries({ ...themes.global, ...theme })) {
			let _value = userPrimaryColor && primaries.includes(field) ? userPrimaryColor : (userSecondaryColor && secondaries.includes(field) ? userSecondaryColor : value);
			// if (userPrimaryColor && themeName.name === 'classic') {
			// 	if (field === 'menuBackgroundColor') {
			// 		_value = "#292929ff";
			// 	}
			// }
			document.documentElement.style.setProperty(`--${field}`, _value);
		}
	};

	const changeTheme = (themeFromDatabase) => {
		const themeName = parseThemeName(themeFromDatabase);
		save({ ...lastSessionData, id: SESSION_STORE, theme: themeName }, SESSION_STORE);
		setTheme(themes[themeName]);
		setThemeName({ name: themeName });
	};

	return (
		<ThemeContext.Provider value={{ changeTheme, themeName }}>
			{children}
		</ThemeContext.Provider>
	);
};

export function useTheme() {
	const context = useContext(ThemeContext);

	return context;
}
